import {
  DEMO_AGENT_PROFILES,
  DEMO_UNIT_COUNTS,
  DEMO_AGENT_ROLES,
  DEMO_SCENARIOS,
  type DemoScenarioKey,
} from "@/api/mock-data/investment-banking/scenarios";
import {
  AgentBusinessUnit,
  AgentEnvironment,
  AgentEnvironmentType,
  AgentPlatform,
  AgentProject,
  AgentPurpose,
  AgentSecurityStatus,
  AgentTechnology,
} from "@/api/temp/AgentEstateService";
import { isInvestmentBankingDemo } from "@/libs/investmentBankingDemo";
import {
  AGENT_NAME_PREFIXES,
  BUSINESS_UNIT_DEFINITIONS,
  BUSINESS_UNIT_LABELS,
  BUSINESS_UNIT_ORDER,
  ENVIRONMENT_TIER_MIX,
  estateTotals,
  MODELS,
  OWNERS,
  PURPOSE_DESCRIPTIONS,
  PURPOSE_LABELS,
  PurposeMixRow,
  REGIONS,
  unitTotals,
} from "./seed";

/**
 * One generated agent. `settledStatus` is the status the agent reports once
 * classification has reached it; before that it is NOT_EVALUATED, and before
 * discovery reaches it the agent is simply not part of the estate yet.
 *
 * Nothing here is ever mutated - applied policies and access suspensions live
 * in separate maps on the store, so a progress tick can never clobber one.
 */
export interface GeneratedAgent {
  demoScenario?: DemoScenarioKey;
  /**
   * The named-agent key (a scenario key or a `DEMO_AGENT_PROFILES` key). It is
   * also the id suffix, so policy mappings can name agents without knowing the
   * environment id.
   */
  demoKey?: string;
  id: string;
  name: string;
  platform: AgentPlatform;
  businessUnit: AgentBusinessUnit;
  /** Fixed-vocabulary classification. */
  purpose: AgentPurpose;
  /** The free-text sentence describing what this agent does. */
  purposeDescription: string;
  connectedResource: AgentTechnology;
  project: AgentProject;
  /** This agent's own deployment tier, not the connection's. */
  environmentType: AgentEnvironmentType;
  region: string;
  owner: string;
  model: string;
  identityName: string;
  settledStatus: AgentSecurityStatus;
  /**
   * 0-based position after interleaving. Both the discovered and the
   * classified cutoffs compare against this, so "has discovery reached me"
   * and "has classification reached me" are answered by the same ordering the
   * list renders in.
   */
  sequenceIndex: number;
  /** Minutes before the environment start that this agent was last observed. */
  lastObservedOffsetMinutes: number;
  /** Days before the environment start that this agent was registered. */
  createdAtOffsetDays: number;
}

/** Deterministic 32-bit PRNG so one environment always yields the same estate. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

interface AgentSlot {
  purpose: AgentPurpose;
  technology: AgentTechnology;
  settledStatus: AgentSecurityStatus;
}

/**
 * Expand one business unit's purpose mix into per-agent slots, interleaved
 * rather than blocked.
 *
 * Interleaving matters twice over: discovery reveals agents in array order, so
 * a blocked sequence would show every secure agent first and make the estate
 * look fully secure until the end of the run - and it would reveal the whole
 * Research gap in one lump at the very end.
 */
function expandPurposeMix(businessUnit: AgentBusinessUnit): AgentSlot[] {
  const { purposeMix } = BUSINESS_UNIT_DEFINITIONS[businessUnit];

  const buckets = purposeMix
    .flatMap((row: PurposeMixRow) => [
      {
        purpose: row.purpose,
        technology: row.technology,
        settledStatus: "SECURE" as AgentSecurityStatus,
        remaining: row.secure,
      },
      {
        purpose: row.purpose,
        technology: row.technology,
        settledStatus: "NOT_SECURE" as AgentSecurityStatus,
        remaining: row.notSecure,
      },
    ])
    .filter(bucket => bucket.remaining > 0);

  const total = unitTotals(businessUnit).total;
  const slots: AgentSlot[] = [];

  // Largest-remainder round robin: each pass emits from every bucket that
  // still has entries, in proportion to how many it started with.
  while (slots.length < total) {
    let emitted = false;
    for (const bucket of buckets) {
      if (bucket.remaining <= 0) continue;
      slots.push({
        purpose: bucket.purpose,
        technology: bucket.technology,
        settledStatus: bucket.settledStatus,
      });
      bucket.remaining -= 1;
      emitted = true;
      if (slots.length === total) break;
    }
    if (!emitted) break;
  }
  return slots;
}

/**
 * Curated examples use the same generator, identities and status distribution.
 * All 14 generation-order sequence numbers are curated - `sequence` here is
 * the per-business-unit generation counter (HR first, then Customer Support,
 * Finance, Engineering, Analytics, per `BUSINESS_UNIT_ORDER`), not the
 * interleaved `sequenceIndex` the estate renders in.
 *
 * Sequence 2 is HR's Not Secure slot, sequence 5 is Client Services' Not Secure
 * slot (see `BUSINESS_UNIT_DEFINITIONS` in `./seed`) - both names and identity
 * strings are kept from the pre-rescale estate on purpose, since several
 * Cypress specs (GovernanceJourney, PolicyApplicationContract, EstateSearch)
 * still address these two agents by name.
 */
const CURATED_AGENTS: Record<string, Partial<GeneratedAgent>> = {
  // HR / SharePoint (mcp-sharepoint-kyc, 7 tools).
  "HR:0": {
    name: "Employee Onboarding Assistant",
    purposeDescription:
      "Guides new-hire paperwork and benefits enrollment for Human Capital.",
    identityName: "svc-employee-onboarding",
  },
  "HR:1": {
    name: "HR Benefits Assistant",
    purposeDescription:
      "Compensation & Benefits — explains benefits and eligibility",
    identityName: "svc-hr-benefits",
    owner: "People Systems",
  },
  "HR:2": {
    name: "Talent Acquisition Assistant",
    purposeDescription:
      "Summarizes candidate applications and interview feedback for recruiters.",
    identityName: "svc-talent-acquisition",
  },
  // Customer Support / Internal Knowledge Base (mcp-kb-private-banking, 4 tools).
  "CUSTOMER_SUPPORT:0": {
    name: "Private Client Service Assistant",
    purposeDescription:
      "Resolves relationship-manager service requests from the private banking knowledge base.",
    identityName: "svc-private-client-service",
  },
  "CUSTOMER_SUPPORT:1": {
    name: "Customer Support agent",
    purposeDescription:
      "Employee and customer support — answers service questions from the support knowledge base, scoped away from finance-classified data.",
    identityName: "svc-customer-support",
    owner: "Customer Operations",
  },
  // Customer Support / Microsoft 365 (mcp-m365-rm-workspace, 3 tools).
  "CUSTOMER_SUPPORT:2": {
    name: "Relationship Manager Correspondence Assistant",
    purposeDescription:
      "Drafts client correspondence and meeting summaries from Microsoft 365 content.",
    identityName: "svc-rm-correspondence",
  },
  // Finance / Snowflake (mcp-snowflake-markets, 5 tools) and Finance /
  // Databricks (mcp-databricks-risk-models, 5 tools). The generator's
  // round-robin over FINANCE's purposeMix lands sequence 7 and 9 on Snowflake
  // (both Secure) and sequence 8 on Databricks (Not Secure) - not 7/8 Secure
  // then 9 Not Secure as a naive reading of the mix table would suggest - so
  // the names below are assigned to the slot each one's technology/status
  // actually lands on, not to the sequence number itself.
  "FINANCE:0": {
    name: "Trading Desk P&L Assistant",
    purposeDescription:
      "Reports daily trading desk profit and loss from the markets data warehouse.",
    identityName: "svc-trading-pnl",
  },
  "FINANCE:1": {
    name: "Trade Settlement Reconciliation Assistant",
    purposeDescription:
      "Reconciles trade settlement breaks against the clearing ledger.",
    identityName: "svc-trade-settlement",
  },
  "FINANCE:2": {
    name: "Counterparty Exposure Assistant",
    purposeDescription:
      "Analyzes counterparty exposure and margin utilization across trading books.",
    identityName: "svc-counterparty-exposure",
  },
  // Engineering / GitHub (mcp-github-trading-platform, 5 tools) and
  // Engineering / Databricks (mcp-databricks-trading-systems, 5 tools).
  "ENGINEERING:0": {
    name: "Trading Platform Release Assistant",
    purposeDescription:
      "Reviews pull requests and triages releases for the trading platform codebase.",
    identityName: "svc-trading-release",
  },
  "ENGINEERING:1": {
    name: "Market Data Pipeline Assistant",
    purposeDescription:
      "Monitors and patches the market-data ingestion pipeline repositories.",
    identityName: "svc-market-data-pipeline",
  },
  "ENGINEERING:2": {
    name: "Trading Systems Data Platform Assistant",
    purposeDescription:
      "Administers data platform jobs supporting trading systems infrastructure.",
    identityName: "svc-trading-systems-platform",
  },
  // Analytics / Snowflake (mcp-snowflake-risk, 5 tools) and Analytics /
  // Internal Knowledge Base (mcp-kb-research, 3 tools).
  "ANALYTICS:0": {
    name: "Churn Analytics agent",
    purposeDescription:
      "Aggregates customer segment metrics — counts, balances and AUM, and activity levels — from the governed risk warehouse to surface churn drivers.",
    identityName: "svc-churn-analytics",
  },
  "ANALYTICS:1": {
    name: "Equity & Credit Research Assistant",
    purposeDescription:
      "Synthesizes equity and credit research notes into briefing summaries.",
    identityName: "svc-equity-credit-research",
  },
};

/**
 * Build the estate for one environment. Called exactly once per environment,
 * at start-onboarding time; the store caches the result for the session.
 *
 * Every agent lands on the environment's own platform - TrustLogix discovered
 * them from that connection, and inventing a federated tail meant the platform
 * card could not be traced back to anything the administrator connected.
 */
export function generateAgents(
  environment: AgentEnvironment,
): GeneratedAgent[] {
  // Seeded from the connection's identity, not its generated id - the id
  // carries a timestamp, which would make every run produce a different estate
  // and break both reproducible screenshots and exact-count assertions.
  const random = mulberry32(
    hashString(`${environment.platform}:${environment.name}`),
  );
  const agents: GeneratedAgent[] = [];
  // Cycled estate-wide rather than per business unit, so every unit carries a
  // representative spread of tiers instead of one unit being entirely test.
  const tierCycle = ENVIRONMENT_TIER_MIX[environment.environmentType];
  let sequence = 0;

  for (const businessUnit of BUSINESS_UNIT_ORDER) {
    const baseSlots = expandPurposeMix(businessUnit);
    const slots = isInvestmentBankingDemo()
      ? Array.from({ length: DEMO_UNIT_COUNTS[businessUnit] }, (_, i) => ({
          ...baseSlots[i % baseSlots.length],
        }))
      : baseSlots;

    const projects = BUSINESS_UNIT_DEFINITIONS[businessUnit].projects;

    slots.forEach((slot, index) => {
      const descriptions = PURPOSE_DESCRIPTIONS[slot.purpose];
      const purposeDescription = descriptions[index % descriptions.length];
      const prefix =
        AGENT_NAME_PREFIXES[Math.floor(random() * AGENT_NAME_PREFIXES.length)];
      sequence += 1;
      agents.push({
        id: `${environment.id}-agent-${sequence}`,
        name: `${prefix} ${PURPOSE_LABELS[slot.purpose]} ${String(sequence).padStart(4, "0")}`,
        platform: environment.platform,
        businessUnit,
        purpose: slot.purpose,
        purposeDescription,
        connectedResource: slot.technology,
        // Both cycle on a counter rather than `random()`: the PRNG is consumed
        // positionally below, so drawing here would shift every later value and
        // rename all 149 agents, breaking the exact-count specs.
        project: projects[index % projects.length],
        environmentType: tierCycle[sequence % tierCycle.length],
        region: REGIONS[Math.floor(random() * REGIONS.length)],
        owner: OWNERS[Math.floor(random() * OWNERS.length)],
        model: MODELS[Math.floor(random() * MODELS.length)],
        identityName: `svc-${BUSINESS_UNIT_LABELS[businessUnit]
          .toLowerCase()
          .replace(/[^a-z]+/g, "-")}-${String(sequence).padStart(4, "0")}`,
        settledStatus: slot.settledStatus,
        // Overwritten once the whole estate is interleaved.
        sequenceIndex: 0,
        lastObservedOffsetMinutes: Math.floor(random() * 720),
        createdAtOffsetDays: 30 + Math.floor(random() * 300),
        ...(isInvestmentBankingDemo()
          ? demoAgentValues(businessUnit, index, environment)
          : CURATED_AGENTS[`${businessUnit}:${index}`]),
      });
    });
  }

  // Interleave across business units so what discovery reveals at any moment is
  // a representative slice of the whole estate, not one unit at a time. Only
  // after this is the final order known, so `sequenceIndex` is stamped here.
  return interleaveByBusinessUnit(agents).map((agent, index) => ({
    ...agent,
    sequenceIndex: index,
  }));
}

function interleaveByBusinessUnit(agents: GeneratedAgent[]): GeneratedAgent[] {
  const byUnit = new Map<AgentBusinessUnit, GeneratedAgent[]>();
  for (const agent of agents) {
    const bucket = byUnit.get(agent.businessUnit);
    if (bucket) bucket.push(agent);
    else byUnit.set(agent.businessUnit, [agent]);
  }

  const queues = BUSINESS_UNIT_ORDER.map(
    businessUnit => byUnit.get(businessUnit) ?? [],
  ).filter(queue => queue.length > 0);

  const result: GeneratedAgent[] = [];
  let cursor = 0;
  while (result.length < agents.length) {
    let emitted = false;
    for (const queue of queues) {
      if (cursor >= queue.length) continue;
      result.push(queue[cursor]);
      emitted = true;
    }
    if (!emitted) break;
    cursor += 1;
  }
  return result;
}

/** 14 - the one number the whole demo reconciles against. */
export const TOTAL_AGENT_COUNT = estateTotals().total;

export const getTotalAgentCount = (): number =>
  isInvestmentBankingDemo()
    ? Object.values(DEMO_UNIT_COUNTS).reduce((sum, count) => sum + count, 0)
    : TOTAL_AGENT_COUNT;

function demoAgentValues(
  unit: AgentBusinessUnit,
  index: number,
  environment: AgentEnvironment,
): Partial<GeneratedAgent> {
  const scenarioKey =
    index === 0
      ? unit === "CUSTOMER_SUPPORT"
        ? "customer-support"
        : unit === "ANALYTICS"
          ? "churn-analytics"
          : undefined
      : undefined;
  const scenario = scenarioKey ? DEMO_SCENARIOS[scenarioKey] : undefined;
  const profile = scenarioKey
    ? undefined
    : DEMO_AGENT_PROFILES[`${unit}:${index}`];
  const demoKey = scenarioKey ?? profile?.key;
  const roles = DEMO_AGENT_ROLES[unit];
  const role = roles[index % roles.length];
  const variant = ["Americas", "EMEA", "APAC"][
    Math.floor(index / roles.length) % 3
  ];
  const named = scenario ?? profile;
  return {
    id: `${environment.id}-${demoKey ?? `${unit.toLowerCase()}-${index + 1}`}`,
    name:
      named?.name ??
      `${role} — ${variant} ${Math.floor(index / (roles.length * 3)) + 1}`,
    identityName: `svc-${demoKey ?? `${unit.toLowerCase()}-${index + 1}`}`,
    owner: BUSINESS_UNIT_LABELS[unit],
    purposeDescription:
      named?.mission ??
      `${role} supports ${BUSINESS_UNIT_LABELS[unit]} with approved information and human-reviewed Investment Banking workflows.`,
    ...(named
      ? {
          demoKey,
          purpose: named.purpose,
          connectedResource: named.technology,
        }
      : {}),
    ...(scenario
      ? { demoScenario: scenarioKey, settledStatus: "NOT_SECURE" as const }
      : {}),
    // Profiles already carry their policy, so they settle Secure.
    ...(profile ? { settledStatus: "SECURE" as const } : {}),
  };
}
